/**
 * Trip CO2 Calculator — lead-in headline under the map.
 *
 * Bridges the map (geography) and the chart (numbers) with one
 * sentence framing the comparison. Two framings:
 *
 *   • **Non-train user**: pitches the train as a reduction lever.
 *     "By going by 🚄 train, you'd reduce this trip's climate impact
 *     by about X%."  — where X is the chosen-vs-train delta.
 *
 *   • **Train user**: celebrates the choice rather than awkwardly
 *     comparing to itself. Comparator flips to plane-only.
 *     "Your 🚄 train trip emits about X% less CO₂ than flying."
 *
 * Renders nothing when there's no useful delta to report (e.g. the
 * comparator row is missing, or the percentage is non-positive).
 *
 * Phase 6 (deferred from Phase 5) — PRD §5.4 item 4 / §6.3, with
 * the dropdown simplified to a fixed train-vs-chosen framing for v1.
 */

import React from 'react';
import type { ChartRow } from './buildComparison';
import { MODE_DISPLAY, type TransportMode } from './modes';

export interface ComparisonLeadInProps {
  rows: ChartRow[];
  userMode: TransportMode;
}

const ComparisonLeadIn: React.FC<ComparisonLeadInProps> = ({ rows, userMode }) => {
  const chosen = rows.find((r) => r.isChosen);
  if (!chosen) return null;

  const trainEmoji = MODE_DISPLAY.train.emoji;

  // Train chosen → compare against the plane row (positive framing).
  if (userMode === 'train') {
    const plane = rows.find((r) => r.id === 'airplane');
    if (!plane || plane.kg <= 0) return null;
    const percent = Math.round(((plane.kg - chosen.kg) / plane.kg) * 100);
    if (percent <= 0) return null;
    return (
      <LeadInFrame variant="celebratory">
        Your {trainEmoji} train trip emits about <b>{percent}% less</b> CO₂ than the same
        trip by plane.
      </LeadInFrame>
    );
  }

  // Any other mode → compare against the train row (reduction framing).
  const train = rows.find((r) => r.id === 'train');
  if (!train || chosen.kg <= 0) return null;
  const percent = Math.round(((chosen.kg - train.kg) / chosen.kg) * 100);
  if (percent <= 0) return null;
  return (
    <LeadInFrame variant="invite">
      By going by {trainEmoji} <b>train</b>, you'd reduce this trip's transport climate
      impact by about <b>{percent}%</b>.
    </LeadInFrame>
  );
};

export default ComparisonLeadIn;

// ─────────────────────────────────────────────────────────────────────────────
// Visual wrapper — same shape for both framings, differs only in subtle accent
// ─────────────────────────────────────────────────────────────────────────────

const LeadInFrame: React.FC<{
  variant: 'invite' | 'celebratory';
  children: React.ReactNode;
}> = ({ variant, children }) => (
  <p
    style={{
      // Surface tokens kept in sync with ResultChart, PerspectiveSection,
      // and CantTrainDialog so the four sections between the map and the
      // share row read as one visual family. Celebratory variant keeps
      // its green tint (only used when the user's chosen mode is train —
      // a positive moment worth visually celebrating).
      margin: '1.5rem 0 0',
      padding: '0.85rem 1rem',
      background: variant === 'celebratory' ? 'hsl(134, 55%, 94%)' : '#fafafa',
      border: `1px solid ${variant === 'celebratory' ? 'hsl(134, 55%, 72%)' : '#e2e8f0'}`,
      borderRadius: 10,
      fontSize: 14,
      lineHeight: 1.5,
      color: '#0f172a',
      textAlign: 'center',
    }}
  >
    {children}
  </p>
);
